'use client';

import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { chakra } from '@ironhack/design-system2/components/chakra';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { type ReactElement, useEffect, useRef, useState } from 'react';

export type HeaderFormProps = {
  ironformId: string;
  ironformType: string;
};

type Props = {
  onSubmit?: () => void;
  pageUrl: string;
} & HeaderFormProps &
  BoxProps;

type Pixels = `${number}px`;

// Needs forward ref to expose element to parent for scrolling
export const HeaderForm = forwardRef((props: Props, ref): ReactElement => {
  const { ironformId, ironformType, onSubmit, pageUrl, ...boxProps } = props;

  const url = new URL('https://ironforms.ironhack.com');
  url.searchParams.append('env', 'prod');
  url.searchParams.append('formType', ironformType);
  url.searchParams.append('ironform_id', ironformId);
  if (pageUrl) url.searchParams.append('parentUrl', pageUrl);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<Pixels | undefined>();
  const [loadCount, setLoadCount] = useState(0);

  const updateLoads = (): void => {
    const updatedLoadCount = loadCount + 1;
    setLoadCount(updatedLoadCount);

    if (updatedLoadCount > 1) onSubmit?.();
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent<{ frameHeight: number }>): void => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { frameHeight } = event.data;

      // Add extra height to avoid scrollbars and avoid cutting thank you screen
      setHeight(`${frameHeight + 50}px`);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef, setHeight]);

  return (
    // This extra is box is to add padding without messing with the height math
    <Box {...boxProps} ref={ref}>
      <chakra.iframe
        onLoad={updateLoads}
        ref={iframeRef}
        src={url.href}
        style={{ height }}
        transition="height 0.25s"
        w="full"
      />
    </Box>
  );
});
